// src/utils/lazyComponents.js
// Lazy loading utility for code splitting

import { lazy } from 'react';

// Enhanced retry mechanism for failed lazy loads with better error handling
export const lazyWithRetry = (componentImport, maxRetries = 3) => {
  return lazy(async () => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await componentImport();
      } catch (error) {
        lastError = error;
        console.warn(`Lazy load attempt ${attempt} failed:`, error);
        
        // Handle chunk loading errors specifically
        if (error.name === 'ChunkLoadError' || error.message.includes('Loading chunk')) {
          console.error('Chunk loading failed. This might be due to:');
          console.error('1. Network connectivity issues');
          console.error('2. Cached old chunks after deployment');
          console.error('3. Server routing configuration issues');
          
          // For chunk load errors, try to reload the page if it's the last attempt
          if (attempt === maxRetries) {
            console.error('All chunk load attempts failed. Suggesting page reload...');
            
            // Show user-friendly error message and suggest page reload
            if (window.confirm('Failed to load application resources. Would you like to reload the page?')) {
              window.location.reload();
              return; // This will never execute but prevents further error throwing
            }
          }
        }
        
        if (attempt < maxRetries) {
          // Wait before retrying with exponential backoff
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // For chunk errors, try to clear the module cache
          if (error.name === 'ChunkLoadError') {
            // Clear webpack module cache if available
            if (window.__webpack_require__ && window.__webpack_require__.cache) {
              Object.keys(window.__webpack_require__.cache).forEach(key => {
                delete window.__webpack_require__.cache[key];
              });
            }
          }
        }
      }
    }
    
    // All retries failed, throw the last error
    throw lastError;
  });
};

// Loading fallback component
export const LazyLoadingFallback = ({ component = "Component" }) => (
  <div className="flex items-center justify-center p-4 text-gray-400">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
    <span className="ml-2">Loading {component}...</span>
  </div>
);

// Code Editor Components (Heavy)
export const MonacoEditor = lazy(() => 
  import('@monaco-editor/react').then(module => ({ default: module.Editor }))
);

export const MonacoDiffEditor = lazy(() => 
  import('@monaco-editor/react').then(module => ({ default: module.DiffEditor }))
);

// Heavy Feature Components
export const GitLens = lazy(() => import('../components/GitLens'));
export const ChatInterface = lazy(() => import('../components/ChatInterface'));
export const PreviewPane = lazy(() => import('../components/PreviewPane'));
export const ModernBrowserPreview = lazy(() => import('../components/ModernBrowserPreview'));
export const BrowserDevTools = lazy(() => import('../components/BrowserDevTools'));

// Complex Page Components
export const ProjectPlanning = lazy(() => import('../pages/ProjectPlanning'));

// Terminal Components
export const TerminalComponent = lazy(() => import('../components/TerminalComponent'));

// Code Context Features
export const CodeContextViewer = lazy(() => import('../components/CodeContextViewer'));

// Syntax Highlighting (Heavy)
export const MarkdownRenderer = lazy(() => import('../components/MarkdownRenderer'));

// Loading Component for Lazy Components
export const ComponentLoader = ({ children, fallback = null }) => {
  return (
    <div className="component-loader">
      {fallback || (
        <div className="flex items-center justify-center p-4 text-gray-400">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
          <span className="ml-2">Loading...</span>
        </div>
      )}
      {children}
    </div>
  );
};
